/** Port von `IwmDialogContainerDuplizieren.razor`. */

import { Fragment, useCallback, useEffect, useState } from 'react';

import { containerApi } from '@/api/services/containerApi';
import { Button, Dialog, ProgressButton } from '@/components/ui';
import type { ContainerCloneDefinition, ContainerCloneDefinitionItem } from '@/types/definitions';

import { ContainerCloneItem, ContainerCloneSource } from './ContainerCloneFields';
import { useDialogSource } from './useDialogSource';

export interface ContainerCloneDialogProps {
  open: boolean;
  onClose: () => void;
  viewId: number;
  containerId: number;
  onSuccess: () => void;
}

/** `new ContainerCloneDefinitionItem()`. */
function createClone(): ContainerCloneDefinitionItem {
  return { ContainerNo: null, ContTyp: null, Plombe: null };
}

export function ContainerCloneDialog(props: ContainerCloneDialogProps) {
  const { containerId, onClose, onSuccess, open, viewId } = props;
  const [definition, setDefinition] = useState<ContainerCloneDefinition | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const source = useDialogSource(
    open,
    useCallback(() => containerApi.getEntry(viewId, containerId), [containerId, viewId]),
  );

  // Port von `New()`.
  useEffect(() => {
    if (!open) return;
    setDefinition({ SourceId: containerId, Duplikate: [createClone()] });
    setErrorMessage(null);
    setSaving(false);
  }, [containerId, open]);

  const valid = definition !== null && definition.Duplikate.length > 0 && definition.SourceId > 0;

  const addClone = () => {
    setDefinition((current) =>
      current === null ? current : { ...current, Duplikate: [...current.Duplikate, createClone()] },
    );
  };

  const updateClone = (index: number, patch: Partial<ContainerCloneDefinitionItem>) => {
    setDefinition((current) =>
      current === null
        ? current
        : {
            ...current,
            Duplikate: current.Duplikate.map((item, position) =>
              position === index ? { ...item, ...patch } : item,
            ),
          },
    );
  };

  const save = async () => {
    if (definition === null || !valid) return;
    setSaving(true);
    const result = await containerApi.clone(viewId, definition);
    // Der Altclient lässt `Saving` im Fehlerfall stehen; hier würde das den Speichern-Knopf
    // dauerhaft sperren, deshalb wird das Kennzeichen in jedem Fall zurückgesetzt.
    setSaving(false);
    if (result.success) {
      setDefinition({ SourceId: 0, Duplikate: [] });
      onClose();
      onSuccess();
    } else {
      setErrorMessage(result.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width="650px"
      showCloseIcon
      isModal
      header="Duplicate Container"
    >
      {containerId > 0 && source !== null && definition !== null ? (
        <>
          <div className="row">
            <ContainerCloneSource source={source} />
            <div className="col-lg-12">
              {definition.Duplikate.map((item, index) => (
                <Fragment key={index}>
                  <ContainerCloneItem
                    item={item}
                    index={index}
                    onChange={(patch) => {
                      updateClone(index, patch);
                    }}
                  />
                </Fragment>
              ))}
            </div>
            {errorMessage === null ? null : (
              <div className="col-lg-12">
                <div className="e-error">{errorMessage}</div>
              </div>
            )}
          </div>
          <div className="row" style={{ marginTop: '1rem', marginBottom: '1rem', float: 'right' }}>
            <div className="col-lg-12">
              <Button content="Duplikat hinzufügen" onClick={addClone} />
              <ProgressButton
                content="Save"
                primary
                type="submit"
                progress={saving}
                disabled={!valid}
                onClick={save}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="e-error">Bitte wählen Sie einen Container aus</div>
      )}
    </Dialog>
  );
}

export default ContainerCloneDialog;
